import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import LeaseInsightWidget from '../components/blog/LeaseInsightWidget';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../lib/i18n';

const DemoPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "60-Second Walkthrough: Commercial Lease Abstraction & CAM Audit | CRE-Dominion";
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white px-4 sm:px-6 py-2 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center no-underline">
            <img 
              src={logo} 
              alt="CRE-Dominion Logo" 
              className="h-20 sm:h-24 md:h-28 w-auto object-contain mr-4 hover:opacity-95 transition-opacity" 
            />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <LanguageSwitcher compact />
            <Link to="/login" className="text-sm font-semibold text-slate-600 hover:text-slate-900 hidden sm:inline-block">
              {t("demoPage.signIn", "Sign In")}
            </Link>
            <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2 rounded-lg transition shadow-sm whitespace-nowrap">
              {t("demoPage.requestPilot", "Request Pilot")}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-6 py-12 w-full">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-block bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
            {t("demoPage.badge", "60-Second Interactive Sandbox")}
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
            {t("demoPage.title", "Commercial Lease Abstraction & CAM Audit in Seconds")}
          </h1>
          <p className="text-base md:text-lg text-slate-600 font-medium leading-relaxed">
            {t("demoPage.subtitle", "Automatically audit commercial lease agreements, CAM reconciliation caps, CPI indexation clauses, and rent roll discrepancies from long PDF leases.")}
          </p>
        </div>

        {/* 10-Second Flow */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm text-center">
            <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mx-auto mb-3 font-bold font-mono text-sm">1</div>
            <div className="font-bold text-sm text-slate-900 uppercase tracking-wider mb-1">
              {t("demoPage.step1Title", "Lease Document Drop")}
            </div>
            <p className="text-xs text-slate-500">
              {t("demoPage.step1Desc", "Upload 50+ page commercial leases or paste complex CAM & CPI escalation clauses.")}
            </p>
          </div>

          <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm text-center">
            <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center mx-auto mb-3 font-bold font-mono text-sm">2</div>
            <div className="font-bold text-sm text-slate-900 uppercase tracking-wider mb-1">
              {t("demoPage.step2Title", "AI Clause Abstraction")}
            </div>
            <p className="text-xs text-slate-500">
              {t("demoPage.step2Desc", "Gemini 3.8 Flash extracts base rent, expense stops, CAM caps, index dates, and audit windows.")}
            </p>
          </div>

          <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm text-center">
            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto mb-3 font-bold font-mono text-sm">3</div>
            <div className="font-bold text-sm text-slate-900 uppercase tracking-wider mb-1">
              {t("demoPage.step3Title", "Audit Table & Risks")}
            </div>
            <p className="text-xs text-slate-500">
              {t("demoPage.step3Desc", "Flags overcharges, cumulative CAM compounding traps, and missed indexation adjustments.")}
            </p>
          </div>
        </div>

        {/* Interactive Widget Box */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-12">
          <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-200 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider">
                {t("demoPage.widgetHeader", "Interactive Lease Clause Analyzer")}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {t("demoPage.widgetSub", "Paste a lease clause or upload a lease PDF snippet below to run a real-time extraction.")}
              </p>
            </div>
            <div className="flex gap-2">
              <span className="text-xs font-semibold px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg">
                {t("demoPage.liveBadge", "Live Backend Endpoint")}
              </span>
            </div>
          </div>

          <LeaseInsightWidget slug="demo-walkthrough" />
        </div>

        {/* 3-Bullet Summary & Next Steps */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wider mb-4">
            {t("demoPage.whyTitle", "Why Commercial Asset Managers Rely on CRE-Dominion")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-slate-600">
            <div>
              <div className="font-bold text-slate-900 uppercase mb-1">
                {t("demoPage.benefit1Title", "1. Zero Manual Lease Data Entry")}
              </div>
              <p className="text-xs leading-relaxed">
                {t("demoPage.benefit1Desc", "Turn 80-page master leases into verified key-term abstracts in 30 seconds instead of 4 hours.")}
              </p>
            </div>
            <div>
              <div className="font-bold text-slate-900 uppercase mb-1">
                {t("demoPage.benefit2Title", "2. Catch CAM Overcharges")}
              </div>
              <p className="text-xs leading-relaxed">
                {t("demoPage.benefit2Desc", "Cross-check controllable vs non-controllable expense caps before approving annual reconciliations.")}
              </p>
            </div>
            <div>
              <div className="font-bold text-slate-900 uppercase mb-1">
                {t("demoPage.benefit3Title", "3. Automated CPI Adjustments")}
              </div>
              <p className="text-xs leading-relaxed">
                {t("demoPage.benefit3Desc", "Never miss an annual inflation adjustment or notice window across your commercial portfolio.")}
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {t("demoPage.ctaText", "Ready to audit CRE-Dominion on your own commercial lease portfolio?")}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t("demoPage.ctaSub", "Claim a free 14-day sandbox key. Audit 1 active commercial lease with zero credit card and zero setup.")}
              </div>
            </div>
            <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider px-6 py-3 rounded-lg no-underline whitespace-nowrap shadow-sm">
              {t("demoPage.ctaBtn", "Claim 14-Day Sandbox Key &rarr;")}
            </Link>
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-6 px-6 text-center text-xs text-slate-500">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>© 2026 CRE-Dominion • Sovereign Commercial Real Estate Intelligence</div>
          <div className="flex gap-4">
            <Link to="/privacy" className="text-slate-600 hover:text-slate-900">Privacy</Link>
            <Link to="/terms-of-service" className="text-slate-600 hover:text-slate-900">Terms</Link>
            <Link to="/faq" className="text-slate-600 hover:text-slate-900">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;